<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { PopupBasicHeader } from 'wepin-vue-component'
import { BasicBottomButtons as BBB, NoticeText } from '@/components'
import {
  useAccountQuery,
  useBreakPointsClass,
  useI18nTypes,
  useKeyDown,
} from '@/composables'
import { defaultBreakPoints } from '@/const'
import { useNFTAPIStore, useWalletAPIStore, useWalletStore } from '@/stores'
import { Account } from '@/types'

const emits = defineEmits<{
  (event: 'close'): void
}>()
const { t } = useI18nTypes()

const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const router = useRouter()
const { account } = useAccountQuery()
const walletStore = useWalletStore()
const nftAPIStore = useNFTAPIStore()
const accoutId = ref()

onBeforeMount(() => {
  if (walletStore.selectedWalletId === '') {
    return
  }
  nftAPIStore.fetchNFTList({
    walletId: walletStore.selectedWalletId,
  })
})

const onClickConfirm = async () => {
  const walletAPIStore = useWalletAPIStore()

  // 1. 모든 토큰 계정 삭제 (skipRefresh=true로 중복 갱신 방지)
  if (tokenAccounts.value.length > 0) {
    await Promise.all(
      tokenAccounts.value.map(async (accountToken) => {
        return accountToken.hideAccount(true) // skipRefresh=true
      }),
    )
  }

  if (account.value instanceof Account) {
    accoutId.value = account.value.id
  }

  // 2. 부모 계정 삭제 (skipRefresh=true로 중복 갱신 방지)
  await account.value!.hideAccount(true) // skipRefresh=true

  if (accoutId.value) {
    nftAPIStore.nftListByWalletKey[walletStore.selectedWalletId] =
      nftAPIStore.nftListByWalletKey[walletStore.selectedWalletId]?.filter(
        (nft) => nft.accountId !== accoutId.value,
      )
  }

  // 3. 모든 삭제 완료 후 한 번만 wallet 데이터 새로고침
  await walletAPIStore.fetchWalletList({}, true)

  // 4. nextTick으로 Vue 반응성 업데이트 보장
  await nextTick()

  // 5. 페이지 이동
  router.push(`/dashboard/wallet/${walletStore.selectedWalletId}`)
}
const onClickCancel = () => {
  emits('close')
}

useKeyDown({ confirm: onClickConfirm })

const tokenAccounts = computed(() => {
  if (account.value instanceof Account && account.value.tokens) {
    return account.value.tokens
  }
  return []
})
const nftCount = computed(() => {
  if (account.value instanceof Account && account.value.nfts) {
    return account.value.nfts.length
  }
  return 0
})
const balance = computed(() => {
  if (account.value) {
    return account.value.amount.ethValue.toFixed()
  }
  return '0'
})
const symbol = computed(() => {
  if (account.value?.coin) {
    return account.value.coin.symbol
  }
  return ''
})
const descriptionKeypath = computed(() => {
  const prefix = 'hide_account.description.'
  if (tokenAccounts.value.length > 0 && nftCount.value > 0) {
    return prefix + 'has_token_account_and_nft'
  }
  if (tokenAccounts.value.length > 0) {
    return prefix + 'has_token_account'
  }
  if (nftCount.value > 0) {
    return prefix + 'has_nft'
  }
  if (balance.value !== '0') {
    return prefix + 'has_balance'
  }
  return prefix + 'delete_available'
})
</script>

<template>
  <div class="hide-account" :class="[breakPointsClass]">
    <PopupBasicHeader :title="t('hide_account.title')" :exit="onClickCancel" />
    <div class="popup-body">
      <div
        class="description"
        v-html="t(descriptionKeypath, { count: nftCount, balance, symbol })"
      />
      <NoticeText class="notice-text">
        {{ t('hide_account.notice') }}
      </NoticeText>
      <BBB
        :confirm-text="t('global.delete')"
        @confirm="onClickConfirm"
        @cancel="onClickCancel"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';

.hide-account {
  width: 460px;
  background-color: var(--grey_fff);
  border-radius: 8px;
  box-shadow: 0px 20px 40px rgba(0, 0, 0, 0.2);
  & > .popup-body {
    padding: {
      left: 30px;
      right: 30px;
      bottom: 30px;
    }

    & > .description {
      @include typeface('Paragraph_16_r');
      margin-bottom: 20px;
      :deep(b) {
        font-weight: 600;
      }
    }
    & > .notice-text {
      margin-bottom: 20px;
    }
  }
}

.hide-account.mobile {
  width: 100%;
  & > .popup-body {
    padding: {
      left: 20px;
      right: 20px;
    }
  }
}
</style>
